import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Phase 16b (#268/#269): admin-editable curve for the multi-shipment
 * pickup-bundle discount. One row per position on a bundled booking;
 * `fractionOff` is the portion of raw pickup price taken OFF (0.5 = 50%).
 * Position 0 is the 1st-shipment anchor — server enforces fractionOff = 0
 * for that row.
 */
export interface PickupBundleTier {
  id: string;
  carrierId: string;
  position: number;
  fractionOff: number;
  label: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdatePickupBundleTierInput {
  position: number;
  fractionOff: number;
  label?: string | null;
}

export function useGetPickupBundleDiscount(carrierId: string | null) {
  const { token } = useSession();

  return useQuery<PickupBundleTier[]>({
    queryKey: ["pickup-bundle-discount", carrierId],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: PickupBundleTier[];
      }>(`${API_URL}/api/v2/admin/pricing/pickup-bundle-discount`, {
        params: { carrierId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    enabled: !!token && !!carrierId,
  });
}

export function useSetPickupBundleDiscount() {
  const { token } = useSession();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      carrierId,
      tiers,
    }: {
      carrierId: string;
      tiers: UpdatePickupBundleTierInput[];
    }): Promise<PickupBundleTier[]> => {
      const { data } = await axios.put<{
        success: boolean;
        data: PickupBundleTier[];
      }>(
        `${API_URL}/api/v2/admin/pricing/pickup-bundle-discount`,
        { carrierId, tiers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ["pickup-bundle-discount", variables.carrierId],
      });
    },
  });
}
